/*
 * COPYRIGHT MOTOROLA SOLUTIONS, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable } from '@angular/core';
import { HealthService } from './health.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EnvironmentService } from './environment.service';
import { ServiceName } from '../../../assets/config/environment';
import { Location, LocationEvent } from '../../location/model/location';
import { map } from 'rxjs/operators';
import { SortFunctions } from '../../call/util/sort-functions';
import { Store } from '@ngrx/store';
import { ClusterUrlProviderService } from './cluster-url-provider.service';
import { CustomHttpParamEncoder } from '../util/custom-http-param-encoder';
import { Call, Message } from 'CalltakingCoreApi';

@Injectable({ providedIn: 'root' })
export class CtcAdapterService extends HealthService {
    private readonly LOCATION_REBID_URL = `/api/v1/locations/rebid`;
    private readonly LOCATION_LATEST_URL = `/api/v1/locations/call/latest`;
    private readonly LOCATION_MANUAL_ALI_URL = `/api/v1/locations/manuallocation/`;
    private readonly API_RECENT_CALLS = '/api/v1/call-history';
    private readonly API_RECENT_LOCKED_CALLS = '/api/v1/call-history/locked';
    private readonly API_RECENT_CALL_LOCK = '/api/v1/call-history/lock';
    private readonly API_RECENT_CALL_UNLOCK = '/api/v1/call-history/unlock';
    private readonly API_RECENT_CALL_MESSAGES = '/api/v1/message-history';

    constructor(
        protected http: HttpClient,
        protected env: EnvironmentService,
        protected store: Store,
        protected clusterUrlProviderService: ClusterUrlProviderService
    ) {
        super(
            http,
            ServiceName.CTC_ADAPTER,
            env.environment.SERVICES[ServiceName.CTC_ADAPTER]
                ? env.environment.SERVICES[ServiceName.CTC_ADAPTER]
                : [
                      {
                          url: '/ctc-adapter',
                          rackId: ''
                      }
                  ]
        );
    }

    public load(callId: string, clusterName: string) {
        let rackId = this.clusterUrlProviderService.getClusterRackId(clusterName);
        let serviceConfiguration = this.endpoints.find((endpoint) => endpoint.rackId === rackId);
        let uri = `${serviceConfiguration?.url ?? this.activeUrl$.value}${this.LOCATION_LATEST_URL}/${callId}`;
        return this.http.get<LocationEvent[]>(uri, {}).pipe(
            map((locationEvents) => locationEvents.map((locationEvent) => ({ ...locationEvent, location: this.cullLocationXml(locationEvent.location) }))),
            map((locationEvents) => this._splitLocationByProvider(locationEvents)),
            map(({ standard, enhanced }) =>
                standard.sort(SortFunctions.newestLocationEventSort).slice(0, 20).concat(enhanced.sort(SortFunctions.newestLocationEventSort).slice(0, 20))
            )
        );
    }

    // the rest endpoint is returning massive xml in the response so let's toss it out before it gets into our state.
    private cullLocationXml(location: Location) {
        return {
            ...location,
            locationXml: null,
            subscriberInfosXml: null,
            adrCommentXml: null,
            serviceInfosXml: null,
            dataProviderInfosXml: null
        } as Location;
    }

    private _splitLocationByProvider = (locationEvents: LocationEvent[]) =>
        locationEvents.reduce(
            (acc, curr) => {
                (curr.provider === 'standard' ? acc.standard : acc.enhanced).push(curr);
                return acc;
            },
            { standard: [] as LocationEvent[], enhanced: [] as LocationEvent[] }
        );

    public rebid(callId: string, rackId: string | undefined) {
        let serviceConfiguration = this.endpoints.find((endpoint) => endpoint.rackId === rackId);
        let uri = `${serviceConfiguration?.url ?? this.activeUrl$.value}${this.LOCATION_REBID_URL}/${callId}`;
        console.log(`Rebid URI: ${uri} for a call: ${callId} and for cluster is: ('${rackId}')`);
        return this.http.post(uri, {});
    }

    public manualQuery(number: string) {
        return this.http.post<LocationEvent>(`${this.activeUrl$.value}${this.LOCATION_MANUAL_ALI_URL}${encodeURIComponent(number)}`, {});
    }

    public requestRecentCalls(maxCalls = 30) {
        let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });
        params = params.append('maxNumberOfCalls', maxCalls);
        return this.http.get<Call[]>(`${this.activeUrl$.value}${this.API_RECENT_CALLS}`, {
            params: params,
            observe: 'body',
            responseType: 'json'
        });
    }

    public requestLockedCalls(maxCalls = 30) {
        let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });
        params = params.append('maxNumberOfCalls', maxCalls);
        return this.http.get<string[]>(`${this.activeUrl$.value}${this.API_RECENT_LOCKED_CALLS}`, {
            params: params,
            observe: 'body',
            responseType: 'json'
        });
    }

    lockCall(callId: string) {
        return this.http.post<void>(`${this.activeUrl$.value}${this.API_RECENT_CALL_LOCK}/${callId}`, {});
    }

    unlockCall(callId: string) {
        return this.http.post<void>(`${this.activeUrl$.value}${this.API_RECENT_CALL_UNLOCK}/${callId}`, {});
    }

    public requestMessages(callId: string) {
        return this.http.get<Message[]>(`${this.activeUrl$.value}${this.API_RECENT_CALL_MESSAGES}/${callId}`, { responseType: 'json' });
    }
}
